import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Storage utility - replace with localStorage in production
const storage = {
  get: (key) => {
    try {
      return sessionStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  },
  set: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Handle storage errors gracefully
    }
  }
};

const SequenceTooltip = ({ 
  id,
  steps = [],
  refs = {},
  startDelay = 1000,
  stepDelay = 500,
  enabled = true,
  onComplete,
  onSkip
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isSequenceActive, setIsSequenceActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  // Check if sequence was already completed/skipped
  useEffect(() => {
    const completed = storage.get(`sequence-${id}-completed`);
    if (!completed && enabled && steps.length > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsSequenceActive(true);
        setCurrentStep(0);
      }, startDelay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [id, startDelay, enabled, steps.length]);

  // Show tooltip when step becomes active
  useEffect(() => {
    if (isSequenceActive && currentStep < steps.length) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, stepDelay);
    } else {
      setIsVisible(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isSequenceActive, currentStep, stepDelay, steps.length]);

  // Update tooltip position when visible
  useEffect(() => {
    if (isVisible && currentStep < steps.length) {
      const currentStepData = steps[currentStep];
      const targetRef = refs[currentStepData.id];
      
      if (targetRef?.current && tooltipRef.current) {
        const updatePosition = () => {
          const targetRect = targetRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          
          let top, left;
          
          switch (currentStepData.position) {
            case 'top':
              top = targetRect.top - tooltipRect.height - 8;
              left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
              break;
            case 'right':
              top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
              left = targetRect.right + 8;
              break;
            case 'left':
              top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
              left = targetRect.left - tooltipRect.width - 8;
              break;
            default: // bottom
              top = targetRect.bottom + 8;
              left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
          }

          // Keep tooltip within viewport
          const padding = 16;
          left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));
          top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));

          setTooltipPosition({ top, left });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);

        return () => {
          window.removeEventListener('scroll', updatePosition);
          window.removeEventListener('resize', updatePosition);
        };
      }
    }
  }, [isVisible, currentStep, steps, refs]);

  const handleNext = () => {
    setIsVisible(false);
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Sequence completed
      handleComplete();
    }
  };

  const handleComplete = () => {
    setIsSequenceActive(false);
    setIsVisible(false);
    storage.set(`sequence-${id}-completed`, 'true');
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkipAll = () => {
    setIsSequenceActive(false);
    setIsVisible(false);
    storage.set(`sequence-${id}-completed`, 'true');
    if (onSkip) {
      onSkip();
    }
  };

  const getArrowStyles = () => {
    if (currentStep >= steps.length) return {};
    
    const position = steps[currentStep].position;
    const arrowSize = 6;
    
    switch (position) {
      case 'top':
        return {
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid rgb(17, 24, 39)`
        };
      case 'right':
        return {
          top: '50%',
          left: '-6px',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid rgb(17, 24, 39)`
        };
      case 'left':
        return {
          top: '50%',
          right: '-6px',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderLeft: `${arrowSize}px solid rgb(17, 24, 39)`
        };
      default: // bottom
        return {
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid rgb(17, 24, 39)`
        };
    }
  };

  if (!isVisible || !isSequenceActive || currentStep >= steps.length) {
    return null;
  }

  const currentStepData = steps[currentStep];

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 bg-gray-900 text-white p-3 rounded-md shadow-lg min-w-64 max-w-80"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`
      }}
    >
      {/* Arrow */}
      <div
        className="absolute w-0 h-0"
        style={getArrowStyles()}
      />
      
      {/* Content */}
      <div>
        {/* Step indicator */}
        {steps.length > 1 && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-300">
              Step {currentStep + 1} of {steps.length}
            </span>
            <div className="flex space-x-1">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i <= currentStep ? 'bg-white' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Main content */}
        <div className="text-sm mb-3">
          {currentStepData.content}
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkipAll}
            className="text-xs text-gray-300 hover:text-white underline"
          >
            Skip tutorial
          </button>
          
          <div className="flex space-x-2">
            <button
              onClick={handleComplete}
              className="text-xs text-gray-300 hover:text-white underline"
            >
              Got it
            </button>
            <button
              onClick={handleNext}
              className="bg-white text-gray-900 text-xs px-3 py-1 rounded hover:bg-gray-100"
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

SequenceTooltip.propTypes = {
  id: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    position: PropTypes.oneOf(['top', 'right', 'bottom', 'left'])
  })).isRequired,
  refs: PropTypes.object.isRequired,
  startDelay: PropTypes.number,
  stepDelay: PropTypes.number,
  enabled: PropTypes.bool,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func
};

export default SequenceTooltip;